// src/App.jsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Layout from './components/Layout';
import CookieBanner from './components/CookieBanner';
import SessionBootstrap from './components/SessionBootstrap';
import NestedNotFound from './components/layout/NestedNotFound';
import { BrandingProvider } from './context/BrandingContext';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import BranchList from './pages/admin/BranchList';
import BranchForm from './pages/admin/BranchForm';
import UserList from './pages/admin/UserList';
import UserForm from './pages/admin/UserForm';
import ManagerList from './pages/admin/ManagerList';
import ManagerForm from './pages/admin/ManagerForm';

// Coach pages
import CoachDashboard from './pages/coach/CoachDashboard';

// Manager pages (role dashboard)
import ManagerDashboard from './pages/manager/ManagerDashboard';
import ManagerSessionsList from "./pages/manager/sessions/SessionsList";
import ManagerAttendanceMark from "./pages/manager/sessions/AttendanceMark";

import RequireAuth from './components/RequireAuth';
import PublicLayout from './components/PublicLayout';
import SetPassword from './pages/SetPassword';

import { isAuthenticated, getNormalizedRole, targetPathForRole, shouldPromptRole } from './utils/auth';
import ManagerBranches from './pages/manager/ManagerBranches';
import ManagerCoachList from './pages/manager/ManagerCoachList';
import ManagerStudentList from './pages/manager/ManagerStudentList';
import MyProfile from './pages/profile/MyProfile';
import CoachList from './pages/admin/CoachList';
import StudentList from './pages/admin/StudentList';
import CoachForm from './pages/admin/CoachForm';
import StudentForm from './pages/admin/StudentForm';
import AdminLevelList from './pages/admin/levels/LevelList';
import AdminLevelFormPage from './pages/admin/levels/LevelForm';
import ManagerLevelList from './pages/manager/levels/LevelList';
import ManagerLevelFormPage from './pages/manager/levels/LevelForm';
import CoachLevelList from './pages/coach/levels/LevelList';
import AdminBatchList from './pages/admin/batches/BatchList';
import AdminBatchFormPage from './pages/admin/batches/BatchForm';
import ManagerBatchList from './pages/manager/batches/BatchList';
import ManagerBatchFormPage from './pages/manager/batches/BatchForm';
import FeePlanForm from './pages/admin/fees/FeePlanForm';   // << added
import AuditLogs from './pages/admin/AuditLogs';
// Admin
import AdminEnrollmentFormPage from './pages/admin/enrollments/EnrollmentForm';
// Manager
import ManagerEnrollmentFormPage from './pages/manager/enrollments/EnrollmentForm';
// Coach
import CoachEnrollmentFormPage from './pages/coach/enrollments/EnrollmentForm';
import AdminEnrollmentList from './pages/admin/enrollments/EnrollmentList';
import ManagerEnrollmentList from './pages/manager/enrollments/EnrollmentList';
import ManagerEnrollmentDetail from './pages/manager/enrollments/EnrollmentDetail';
import CoachEnrollmentList from './pages/coach/enrollments/EnrollmentList';
import CoachEnrollmentDetail from './pages/coach/enrollments/EnrollmentDetail';
import AdminSessionsList from "./pages/admin/sessions/SessionsList";
import AdminAttendanceMark from "./pages/admin/sessions/AttendanceMark";


// Coach pages
import CoachAttendanceMark from './pages/coach/sessions/AttendanceMark';
import CoachSessionsList from './pages/coach/sessions/SessionsList';
import CoachStudentsList from './pages/coach/students/StudentsList';

// Student pages (read-only)
import StudentDashboard from './pages/student/StudentDashboard';
import StudentAttendance from './pages/student/StudentAttendance';
import StudentSessions from './pages/student/StudentSessions';
import StudentEnrollments from './pages/student/StudentEnrollments';

// Role picker page
import RoleSelect from './pages/RoleSelect';
import FeePlans from './pages/admin/fees/FeePlans';
import MyWallet from './pages/student/MyWallet';
import EnrollmentDetail from './pages/admin/enrollments/EnrollmentDetail';
import DiscountsList from './pages/admin/discounts/DiscountsList';
import DiscountForm from './pages/admin/discounts/DiscountForm';
import ManagerFeePlans from './pages/manager/fees/FeePlans';
import CoachFeePlans from './pages/coach/fees/FeePlans';

import AdminAdvancePayment from './pages/admin/AdvancePayment';

import CoachRevenueReport from './pages/admin/reports/CoachRevenueReport';
import AdminAttendanceView from './pages/admin/sessions/AttendanceView';
import ManagerAttendanceView from './pages/manager/sessions/AttendanceView';
import CoachAttendanceView from './pages/coach/sessions/AttendanceView';
import SessionStatement from './pages/student/SessionStatement';
import SessionAttendanceReport from './pages/admin/reports/SessionAttendanceReport';
import UserDetailsPage from './pages/users/UserDetailsPage';
import StudentImportPage from './pages/admin/StudentImportPage';
import StudentBulkImport from './pages/admin/StudentBulkImport';
import LicenseInvalidPage from './pages/LicenseInvalidPage';
import BrandingSettings from './pages/admin/BrandingSettings';
import LicenseSettings from './pages/admin/LicenseSettings';
import HolidayManagement from './pages/admin/HolidayManagement';
import NotificationPreferences from './pages/admin/NotificationPreferences';
import BillingSettings from './pages/admin/BillingSettings';
import RegionalSettings from './pages/admin/RegionalSettings';
import PaymentMethodSettings from './pages/admin/PaymentMethodSettings';
import WhatsAppSettings from './pages/admin/WhatsAppSettings';
import EmailSettings from './pages/admin/EmailSettings';
import ChessCoachSettings from './pages/admin/ChessCoachSettings';
import PlayingPlatformSettings from './pages/admin/PlayingPlatformSettings';
import ProductionReadiness from './pages/admin/ProductionReadiness';
import StudentNotificationPreferences from './pages/student/StudentNotificationPreferences';
import FAQ from './pages/FAQ';
import SessionCalendar from './pages/shared/SessionCalendar';
import ReportsHub from './pages/admin/reports/ReportsHub';
import ReportSettings from './pages/admin/ReportSettings';
import AtRiskStudents from './pages/admin/AtRiskStudents';
import SetupWizard from './pages/SetupWizard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PublicHelp from './pages/PublicHelp';
import RegisterGuardian from './pages/public/RegisterGuardian';
import ConfirmGuardianLink from './pages/public/ConfirmGuardianLink';
import ParentHome from './pages/parent/ParentHome';
import ParentChildNotifications from './pages/parent/ParentChildNotifications';
import RequireSelectedChild from './components/parent/RequireSelectedChild';
import NotificationsPage from './pages/shared/NotificationsPage';
import PlayingHubPage from './pages/playing/PlayingHubPage';
import PlayGamePage from './pages/playing/PlayGamePage';
import MyGamesPage from './pages/playing/MyGamesPage';
import LeaderboardPage from './pages/playing/LeaderboardPage';
import ChallengePage from './pages/playing/ChallengePage';
import LiveGamesPage from './pages/playing/LiveGamesPage';
import FairPlayReviewPage from './pages/playing/FairPlayReviewPage';
import ChessProfilePage from './pages/playing/ChessProfilePage';
import CoachBoardPage from './pages/playing/CoachBoardPage';
import AiCoachPage from './pages/playing/AiCoachPage';
import ClassroomHub from './pages/classroom/ClassroomHub';
import ClassroomLive from './pages/classroom/ClassroomLive';
import ClassroomMeetingPage from './pages/classroom/ClassroomMeetingPage';
import ClassroomLiveSessionRedirect from './pages/classroom/ClassroomLiveSessionRedirect';
import ZoomConnectionPage from './pages/classroom/ZoomConnectionPage';
import ClassroomSettingsPage from './pages/classroom/ClassroomSettingsPage';

function App() {
    const token = localStorage.getItem('token');

    return (
        <BrandingProvider>
            <BrowserRouter>
                <SessionBootstrap />
                <CookieBanner />
                <Routes>
                    <Route path="/license-invalid" element={<LicenseInvalidPage />} />

                    {/* Root -> login or role dashboard (prompt if multi-role & none selected) */}
                    <Route
                        path="/"
                        element={
                            isAuthenticated()
                                ? (shouldPromptRole()
                                    ? <Navigate to="/select-role" replace />
                                    : <Navigate to={targetPathForRole(getNormalizedRole())} replace />)
                                : <Navigate to="/login" replace />
                        }
                    />

                    {/* First-time setup wizard — shown to admin before any data exists, inside Layout so header/logout are visible */}
                    <Route
                        element={
                            <RequireAuth allowedRoles={['ADMIN']}>
                                <Layout />
                            </RequireAuth>
                        }
                    >
                        <Route path="/setup" element={<SetupWizard />} />
                    </Route>

                    {/* Public layout (login + set password) */}
                    <Route path="/" element={<PublicLayout />}>
                        <Route
                            path="login"
                            element={
                                isAuthenticated()
                                    ? (shouldPromptRole()
                                        ? <Navigate to="/select-role" replace />
                                        : <Navigate to={targetPathForRole(getNormalizedRole())} replace />)
                                    : <Login />
                            }
                        />
                        <Route path="set-password/:token" element={<SetPassword />} />
                        <Route path="forgot-password" element={<ForgotPassword />} />
                        <Route path="privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="terms" element={<TermsOfService />} />
                        <Route path="help" element={<PublicHelp />} />
                        <Route path="register-guardian/:token" element={<RegisterGuardian />} />
                        <Route path="confirm-guardian-link/:token" element={<ConfirmGuardianLink />} />
                    </Route>

                    {/* Mount Select Role INSIDE Layout (no allowedRoles) so header/footer appear */}
                    <Route
                        element={
                            <RequireAuth>
                                <Layout />
                            </RequireAuth>
                        }
                    >
                        <Route path="/select-role" element={<RoleSelect />} />
                        <Route path="/users/:userId" element={<UserDetailsPage />} />
                        <Route path="/playing" element={<PlayingHubPage />} />
                        <Route path="/playing/play" element={<PlayGamePage />} />
                        <Route path="/playing/game/:gameId" element={<PlayGamePage />} />
                        <Route path="/playing/spectate/:gameId" element={<PlayGamePage spectateOnly />} />
                        <Route path="/playing/live" element={<LiveGamesPage />} />
                        <Route path="/playing/games" element={<MyGamesPage />} />
                        <Route path="/playing/leaderboard" element={<LeaderboardPage />} />
                        <Route path="/playing/players/:userId" element={<ChessProfilePage />} />
                        <Route path="/playing/coach-board" element={<CoachBoardPage />} />
                        <Route path="/playing/ai-coach" element={<AiCoachPage />} />
                        <Route path="/playing/challenge" element={<ChallengePage />} />
                        <Route
                          path="/playing/fair-play"
                          element={(
                            <RequireAuth allowedRoles={['ADMIN', 'MANAGER', 'COACH']}>
                              <FairPlayReviewPage />
                            </RequireAuth>
                          )}
                        />
                        <Route
                          path="/playing/fair-play/:id"
                          element={(
                            <RequireAuth allowedRoles={['ADMIN', 'MANAGER', 'COACH']}>
                              <FairPlayReviewPage />
                            </RequireAuth>
                          )}
                        />
                        <Route path="/playing/*" element={<NestedNotFound />} />
                    </Route>

                    {/* Admin (ADMIN only) */}
                    <Route
                        path="/admin/*"
                        element={
                            <RequireAuth allowedRoles={['ADMIN']}>
                                <Layout />
                            </RequireAuth>
                        }
                    >
                        <Route index element={<AdminDashboard />} />
                        <Route path="users" element={<UserList />} />
                        <Route path="users/new" element={<UserForm />} />
                        <Route path="users/:id" element={<UserForm />} />
                        <Route path="branches" element={<BranchList />} />
                        <Route path="branches/new" element={<BranchForm />} />
                        <Route path="branches/:id" element={<BranchForm />} />
                        <Route path="managers" element={<ManagerList />} />
                        <Route path="managers/new" element={<ManagerForm />} />
                        <Route path="managers/:id" element={<ManagerForm />} />
                        <Route path="coaches" element={<CoachList />} />
                        <Route path="students" element={<StudentList />} />

                        <Route path="coaches/new" element={<CoachForm />} />
                        <Route path="coaches/:id" element={<CoachForm />} />
                        <Route path="students/new" element={<StudentForm />} />
                        <Route path="students/bulk-import" element={<StudentBulkImport />} />
                        <Route path="students/:id" element={<StudentForm />} />

                        <Route path="levels" element={<AdminLevelList />} />
                        <Route path="levels/new" element={<AdminLevelFormPage />} />
                        <Route path="levels/:id/edit" element={<AdminLevelFormPage />} />

                        <Route path="batches" element={<AdminBatchList />} />
                        <Route path="batches/new" element={<AdminBatchFormPage />} />
                        <Route path="batches/:id/edit" element={<AdminBatchFormPage />} />
                        <Route path="batches/:id/sessions" element={<AdminSessionsList />} />
                        <Route path="profile" element={<MyProfile />} />

                        <Route path="enrollments" element={<AdminEnrollmentList />} />
                        <Route path="enrollments/new" element={<AdminEnrollmentFormPage />} />
                        <Route path="enrollments/:id/edit" element={<AdminEnrollmentFormPage />} />
                        <Route path="enrollments/:id" element={<EnrollmentDetail />} />

                        <Route path="discounts" element={<DiscountsList />} />
                        <Route path="discounts/new" element={<DiscountForm />} />
                        <Route path="discounts/:id/edit" element={<DiscountForm />} />

                        <Route path="sessions" element={<AdminSessionsList />} />
                        <Route path="sessions/new" element={<Navigate to="/admin/sessions" replace />} />
                        <Route path="sessions/:sessionId/attendance" element={<AdminAttendanceMark />} />
                        <Route path="sessions/:sessionId/attendance/view" element={<AdminAttendanceView />} />

                        <Route path="reports/coach-revenue" element={<CoachRevenueReport />} />
                        <Route
                            path="reports/sessions-summary"
                            element={<Navigate to="/admin/reports?tab=sessions-summary" replace />}
                        />
                        <Route
                            path="reports/monthly-statements"
                            element={<Navigate to="/admin/reports?tab=monthly-statements" replace />}
                        />
                        <Route
                            path="reports/session-statement"
                            element={<SessionStatement />}
                        />

                        <Route
                            path="reports/session-attendance"
                            element={<SessionAttendanceReport />}
                        />
                        <Route path="reports" element={<ReportsHub />} />
                        <Route path="calendar" element={<SessionCalendar />} />
                        <Route path="at-risk" element={<AtRiskStudents />} />

                        <Route path="fees/plans" element={<FeePlans />} />
                        <Route path="fees/plans/new" element={<FeePlanForm />} />

                        <Route path="advance-payment" element={<AdminAdvancePayment />} />

                        <Route path="audit-logs" element={<AuditLogs />} />

                        <Route path="student-import" element={<StudentImportPage />} />

                        <Route path="branding" element={<BrandingSettings />} />
                        <Route path="regional-settings" element={<RegionalSettings />} />
                        <Route path="report-settings" element={<ReportSettings />} />
                        <Route path="license" element={<LicenseSettings />} />
                        <Route path="billing-settings" element={<BillingSettings />} />
                        <Route path="payment-methods" element={<PaymentMethodSettings />} />
                        <Route path="whatsapp" element={<WhatsAppSettings />} />
                        <Route path="email-settings" element={<EmailSettings />} />
                        <Route path="ai-coach-settings" element={<ChessCoachSettings />} />
                        <Route path="playing-platform-settings" element={<PlayingPlatformSettings />} />
                        <Route path="production-readiness" element={<ProductionReadiness />} />
                        <Route path="holidays" element={<HolidayManagement />} />
                        <Route path="notification-preferences" element={<NotificationPreferences />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="faq" element={<FAQ />} />
                        <Route path="classroom" element={<ClassroomHub role="ADMIN" />} />
                        <Route path="classroom/live" element={<ClassroomLive role="ADMIN" />} />
                        <Route path="classroom/live/:sessionId" element={<ClassroomLiveSessionRedirect role="ADMIN" />} />
                        <Route path="classroom/meeting/:sessionId" element={<ClassroomMeetingPage role="ADMIN" />} />
                        <Route path="classroom/zoom" element={<ZoomConnectionPage />} />
                        <Route path="classroom/settings" element={<ClassroomSettingsPage />} />
                        <Route path="*" element={<NestedNotFound />} />

                    </Route>

                    {/* Manager (MANAGER only) */}
                    <Route
                        path="/manager/*"
                        element={
                            <RequireAuth allowedRoles={['MANAGER']}>
                                <Layout />
                            </RequireAuth>
                        }
                    >
                        <Route index element={<ManagerDashboard />} />
                        <Route path="branches" element={<ManagerBranches />} />
                        <Route path="coaches" element={<ManagerCoachList />} />
                        <Route path="students" element={<ManagerStudentList />} />
                        <Route path="students/new" element={<StudentForm managerMode />} />
                        <Route path="students/:id" element={<StudentForm managerMode />} />
                        <Route path="advance-payment" element={<AdminAdvancePayment managerMode />} />

                        <Route path="levels" element={<ManagerLevelList />} />
                        <Route path="levels/new" element={<ManagerLevelFormPage />} />
                        <Route path="levels/:id/edit" element={<ManagerLevelFormPage />} />

                        <Route path="batches" element={<ManagerBatchList />} />
                        <Route path="batches/new" element={<ManagerBatchFormPage />} />
                        <Route path="batches/:id/edit" element={<ManagerBatchFormPage />} />
                        <Route path="batches/:id/sessions" element={<ManagerSessionsList />} />
                        <Route path="profile" element={<MyProfile />} />

                        <Route path="enrollments" element={<ManagerEnrollmentList />} />
                        <Route path="enrollments/new" element={<ManagerEnrollmentFormPage />} />
                        <Route path="enrollments/:id" element={<ManagerEnrollmentDetail />} />

                        <Route path="fees/plans" element={<ManagerFeePlans />} />

                        <Route path="sessions" element={<ManagerSessionsList />} />
                        <Route path="sessions/:sessionId/attendance" element={<ManagerAttendanceMark />} />
                        <Route path="sessions/:sessionId/attendance/view" element={<ManagerAttendanceView />} />

                        <Route
                            path="reports/session-statement"
                            element={<SessionStatement />}
                        />
                        <Route
                            path="reports/session-attendance"
                            element={<SessionAttendanceReport />}
                        />
                        <Route path="reports" element={<ReportsHub />} />
                        <Route path="calendar" element={<SessionCalendar />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="faq" element={<FAQ />} />
                        <Route path="classroom" element={<ClassroomHub role="MANAGER" />} />
                        <Route path="classroom/live" element={<ClassroomLive role="MANAGER" />} />
                        <Route path="classroom/live/:sessionId" element={<ClassroomLiveSessionRedirect role="MANAGER" />} />
                        <Route path="classroom/meeting/:sessionId" element={<ClassroomMeetingPage role="MANAGER" />} />
                        <Route path="*" element={<NestedNotFound />} />

                    </Route>

                    {/* Coach (COACH only) */}
                    <Route
                        path="/coach/*"
                        element={
                            <RequireAuth allowedRoles={['COACH']}>
                                <Layout />
                            </RequireAuth>
                        }
                    >
                        <Route index element={<CoachDashboard />} />
                        {/* add coach-specific routes here */}

                        <Route path="levels" element={<CoachLevelList />} />

                        <Route path="profile" element={<MyProfile />} />

                        <Route path="enrollments" element={<CoachEnrollmentList />} />
                        <Route path="enrollments/new" element={<CoachEnrollmentFormPage />} />
                        <Route path="enrollments/:id" element={<CoachEnrollmentDetail />} />
                        <Route path="sessions" element={<CoachSessionsList />} />
                        <Route path="sessions/:sessionId/attendance" element={<CoachAttendanceMark />} />
                        <Route path="sessions/:sessionId/attendance/view" element={<CoachAttendanceView />} />
                        <Route path="students" element={<CoachStudentsList />} />

                        <Route path="fees/plans" element={<CoachFeePlans />} />

                        <Route path="reports" element={<ReportsHub />} />
                        <Route
                            path="reports/session-attendance"
                            element={<Navigate to="/coach/reports?tab=batch-attendance" replace />}
                        />
                        <Route
                            path="reports/session-statement"
                            element={<Navigate to="/coach/reports" replace />}
                        />
                        <Route path="calendar" element={<SessionCalendar />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="faq" element={<FAQ />} />
                        <Route path="classroom" element={<ClassroomHub role="COACH" />} />
                        <Route path="classroom/live" element={<ClassroomLive role="COACH" />} />
                        <Route path="classroom/live/:sessionId" element={<ClassroomLiveSessionRedirect role="COACH" />} />
                        <Route path="classroom/meeting/:sessionId" element={<ClassroomMeetingPage role="COACH" />} />
                        <Route path="*" element={<NestedNotFound />} />

                    </Route>

                    {/* Parent (PARENT only) */}
                    <Route
                        path="/parent/*"
                        element={
                            <RequireAuth allowedRoles={['PARENT']}>
                                <Layout />
                            </RequireAuth>
                        }
                    >
                        <Route index element={<ParentHome />} />
                        <Route path="profile" element={<MyProfile />} />
                        <Route path="faq" element={<FAQ />} />
                        <Route path="classroom" element={<ClassroomHub role="PARENT" />} />
                        <Route path="classroom/live" element={<ClassroomLive role="PARENT" />} />
                        <Route path="classroom/live/:sessionId" element={<ClassroomLiveSessionRedirect role="PARENT" />} />
                        <Route path="classroom/meeting/:sessionId" element={<ClassroomMeetingPage role="PARENT" />} />
                        <Route path="inbox" element={<NotificationsPage />} />
                        <Route element={<RequireSelectedChild />}>
                            <Route path="dashboard" element={<StudentDashboard />} />
                            <Route path="attendance" element={<StudentAttendance />} />
                            <Route path="sessions" element={<StudentSessions />} />
                            <Route path="calendar" element={<SessionCalendar roleProp="PARENT" />} />
                            <Route path="enrollments" element={<StudentEnrollments />} />
                            <Route path="wallet" element={<MyWallet />} />
                            <Route path="notifications" element={<ParentChildNotifications />} />
                            <Route path="session-statement" element={<SessionStatement />} />
                        </Route>
                        <Route path="*" element={<NestedNotFound />} />
                    </Route>

                    {/* Student (STUDENT only) */}
                    <Route
                        path="/student/*"
                        element={
                            <RequireAuth allowedRoles={['STUDENT']}>
                                <Layout />
                            </RequireAuth>
                        }
                    >
                        <Route index element={<StudentDashboard />} />
                        <Route path="attendance" element={<StudentAttendance />} />
                        <Route path="sessions" element={<StudentSessions />} />
                        <Route path="calendar" element={<SessionCalendar roleProp="STUDENT" />} />
                        <Route path="enrollments" element={<StudentEnrollments />} />
                        <Route path="profile" element={<MyProfile />} />

                        <Route path="wallet" element={<MyWallet />} />
                        <Route path="session-statement" element={<SessionStatement />} />
                        <Route path="inbox" element={<NotificationsPage />} />
                        <Route path="notifications" element={<StudentNotificationPreferences />} />
                        <Route path="faq" element={<FAQ />} />
                        <Route path="classroom" element={<ClassroomHub role="STUDENT" />} />
                        <Route path="classroom/live" element={<ClassroomLive role="STUDENT" />} />
                        <Route path="classroom/live/:sessionId" element={<ClassroomLiveSessionRedirect role="STUDENT" />} />
                        <Route path="classroom/meeting/:sessionId" element={<ClassroomMeetingPage role="STUDENT" />} />
                        <Route path="*" element={<NestedNotFound />} />

                    </Route>

                    {/* Catch-all */}
                    <Route
                        path="*"
                        element={
                            isAuthenticated()
                                ? (shouldPromptRole()
                                    ? <Navigate to="/select-role" replace />
                                    : <Navigate to={targetPathForRole(getNormalizedRole())} replace />)
                                : <Navigate to="/login" replace />
                        }
                    />
                </Routes>
            </BrowserRouter>
        </BrandingProvider>
    );
}

export default App;
